import clsx from 'clsx';
import React from 'react';

import { useCompanyLogo } from '../Hooks/useCompanyLogo';
import styles from './CompanyLogoBadge.module.scss';

export interface CompanyLogoBadgeProps {
    /** Company name — used for the first-letter fallback badge. */
    name?: string;
    /** Stored logo URL, if any. */
    logo?: string;
    /** Company website, used to derive a site/Google favicon when no `logo` is set. */
    websiteUrl?: string;
    /** Badge size in px (width & height). Default 24 for table rows. */
    size?: number;
    /** 'square' = rounded square (default), 'circle' = fully round. */
    shape?: 'square' | 'circle';
    className?: string;
    /** Google's generic globe favicon loads at 16x16 — a loaded Google favicon at or under this size is treated as a miss. Default 16. */
    genericFaviconMaxSize?: number;
}

/**
 * Renders a company logo with a graceful fallback chain: it resolves the
 * ordered source chain via `useCompanyLogo` and tries each in turn (advancing
 * on <img> error), and when none resolve, shows a first-letter badge. Mirrors
 * the behavior of KeyCompetitors' CompetitorLogo, unified into one reusable
 * component.
 */
export const CompanyLogoBadge: React.FC<CompanyLogoBadgeProps> = ({
    name,
    logo,
    websiteUrl,
    size = 24,
    shape = 'square',
    className,
    genericFaviconMaxSize = 16,
}) => {
    const { currentSource, handleError, handleLoad } = useCompanyLogo({
        logo,
        websiteUrl,
        genericFaviconMaxSizePx: genericFaviconMaxSize,
    });

    const dimensionStyle: React.CSSProperties = {
        width: size,
        height: size,
        minWidth: size,
        fontSize: Math.max(10, Math.round(size * 0.45)),
    };
    const shapeClass = shape === 'circle' ? styles.Circle : styles.Square;

    if (!currentSource) {
        return (
            <div className={clsx(styles.Badge, styles.Placeholder, shapeClass, className)} style={dimensionStyle} aria-label={name}>
                {name?.charAt(0)?.toUpperCase() || ''}
            </div>
        );
    }

    return (
        <img
            src={currentSource}
            alt={name || 'Company logo'}
            className={clsx(styles.Badge, styles.Image, shapeClass, className)}
            style={dimensionStyle}
            onError={handleError}
            onLoad={handleLoad}
        />
    );
};

export default CompanyLogoBadge;
